import Box from '@components/base/Box'
import { useTheme } from '@libs/useTheme'
import React, { useCallback, useEffect, useRef, useState } from 'react'

const PLACES_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY as string

type PlacePrediction = {
  placeId: string
  text: string
  mainText?: string
  secondaryText?: string
}

export type PlaceDetails = {
  formattedAddress: string
  lat?: number
  lng?: number
  mapUrl?: string
}

export type PlacesAutocompleteInputProps = {
  label?: string
  placeholder?: string
  value?: string
  required?: boolean
  className?: string
  mapUrl?: string
  onChangeText?: (text: string) => void
  onSelect?: (place: PlaceDetails) => void
}

const buildMapsUrl = (formattedAddress: string, placeId: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    formattedAddress
  )}&query_place_id=${encodeURIComponent(placeId)}`

function useDebouncedCallback<T extends (...args: any[]) => void>(
  fn: T,
  delayMs: number
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fnRef = useRef(fn)
  fnRef.current = fn

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => fnRef.current(...args), delayMs)
    },
    [delayMs]
  )
}

const generateSessionToken = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`

export default function PlacesAutocompleteInput({
  label = 'Location',
  placeholder = '123 Main street, London, WC2N 5DU',
  value,
  required,
  className,
  mapUrl,
  onChangeText,
  onSelect,
}: PlacesAutocompleteInputProps) {
  const { theme } = useTheme()

  const [query, setQuery] = useState(value ?? '')
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [error, setError] = useState<string | null>(null)
  const [hasSelectedPlace, setHasSelectedPlace] = useState(
    Boolean(value && mapUrl)
  )
  const [showSelectionError, setShowSelectionError] = useState(false)
  const [currentMapUrl, setCurrentMapUrl] = useState<string | undefined>(
    mapUrl
  )

  const sessionTokenRef = useRef(generateSessionToken())
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setQuery(value ?? '')
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const runSearch = useCallback(async (input: string) => {
    if (!PLACES_API_KEY) {
      setError('Missing Google Places API key')
      setIsLoading(false)
      return
    }

    if (!input || input.trim().length < 3) {
      setSuggestions([])
      setIsOpen(false)
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(
        'https://places.googleapis.com/v1/places:autocomplete',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': PLACES_API_KEY,
          },
          body: JSON.stringify({
            input,
            sessionToken: sessionTokenRef.current,
          }),
        }
      )

      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(
          body?.error?.message || `Request failed (${response.status})`
        )
      }

      const data = await response.json()

      const predictions: PlacePrediction[] = (data?.suggestions ?? [])
        .filter((s: any) => !!s.placePrediction)
        .map((s: any) => ({
          placeId: s.placePrediction.placeId,
          text: s.placePrediction.text?.text ?? '',
          mainText: s.placePrediction.structuredFormat?.mainText?.text,
          secondaryText: s.placePrediction.structuredFormat?.secondaryText?.text,
        }))

      setSuggestions(predictions)
      setIsOpen(predictions.length > 0)
      setHighlightedIndex(-1)
      setError(null)
    } catch (err: any) {
      console.error('Places autocomplete error:', err)
      setSuggestions([])
      setError(err?.message ?? 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const debouncedSearch = useDebouncedCallback(runSearch, 300)

  const handleChange = (data: string) => {
    setQuery(data)
    onChangeText?.(data)
    setHasSelectedPlace(false)
    setShowSelectionError(false)
    setCurrentMapUrl(undefined)
    setIsLoading(true)
    debouncedSearch(data)
  }

  const fetchPlaceDetails = async (
    prediction: PlacePrediction
  ): Promise<PlaceDetails> => {
    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${prediction.placeId}`,
        {
          headers: {
            'X-Goog-Api-Key': PLACES_API_KEY,
            'X-Goog-FieldMask': 'id,formattedAddress,location',
          },
        }
      )
      const data = await response.json()
      return {
        formattedAddress: data?.formattedAddress ?? prediction.text,
        lat: data?.location?.latitude,
        lng: data?.location?.longitude,
        mapUrl: buildMapsUrl(prediction.text, prediction.placeId),
      }
    } catch (err) {
      console.error('Place details error:', err)
      return {
        formattedAddress: prediction.text,
        mapUrl: buildMapsUrl(prediction.text, prediction.placeId),
      }
    }
  }

  const handleSelect = async (prediction: PlacePrediction) => {
    setQuery(prediction.text)
    setHasSelectedPlace(true)
    setShowSelectionError(false)
    setIsOpen(false)
    setSuggestions([])

    const details = await fetchPlaceDetails(prediction)
    const selectedPlace = {
      ...details,
      formattedAddress: prediction.text,
    }
    setQuery(prediction.text)
    setCurrentMapUrl(details.mapUrl)
    onSelect?.(selectedPlace)

    sessionTokenRef.current = generateSessionToken()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => (i + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0) handleSelect(suggestions[highlightedIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <Box
      innerRef={containerRef}
      className={`relative w-full flex flex-col my-3 ${className ?? ''}`}
    >
      {label && (
        <Box
          className={`absolute z-10 text-sm -translate-y-1/2 -translate-x-1 mb-2 ml-2 ${
            required ? 'pl-2' : 'px-2'
          }`}
          style={{ backgroundColor: theme?.backgroundColour }}
        >
          <span className="" style={{ color: theme?.textColour }}>
            {label}
          </span>
          {required && <span style={{ color: '#dc2626' }}> *</span>}
        </Box>
      )}

      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        onBlur={() => {
          setShowSelectionError(Boolean(query.trim()) && !hasSelectedPlace)
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        className="w-full box-border px-4 py-4 text-sm border rounded-2xl outline-none bg-transparent"
        style={{
          borderColor: '#9ca0af',
          backgroundColor: theme?.backgroundColour,
          color: theme?.textColour,
        }}
      />

      {error && (
        <Box className="text-xs mt-1" style={{ color: '#dc2626' }}>
          {error}
        </Box>
      )}

      {showSelectionError && (
        <Box className="text-xs mt-1" style={{ color: '#dc2626' }}>
          Please select an item form the dropdown
        </Box>
      )}

      {currentMapUrl && (
        <Box className="mt-1">
          <a
            href={currentMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs underline"
          >
            View on Google Maps ↗
          </a>
        </Box>
      )}

      <Box
        show={isOpen}
        className="absolute z-50 top-full left-0 right-0 mt-1 rounded shadow-lg border overflow-hidden max-h-64 overflow-y-auto"
        style={{
          borderColor: theme?.borderColour ?? '#d1d5db',
          backgroundColor:
            theme?.backgroundColourSecondary ?? theme?.backgroundColour,
        }}
      >
        {isLoading && (
          <Box className="px-4 py-2 text-sm opacity-60">Searching…</Box>
        )}

        {!isLoading &&
          suggestions.map((s, i) => (
            <Box
              key={s.placeId}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault()
                handleSelect(s)
              }}
              onMouseEnter={() => setHighlightedIndex(i)}
              className="px-4 py-2 cursor-pointer text-sm"
              style={{
                ...(i === highlightedIndex
                  ? { backgroundColor: 'rgba(0,0,0,0.05)' }
                  : undefined),
                ...(i < suggestions.length - 1
                  ? { borderBottom: `1px solid ${theme?.borderColour ?? '#e5e7eb'}` }
                  : undefined),
              }}
            >
              <Box className="font-medium" textColour="textColour">
                {s.mainText ?? s.text}
              </Box>
              {s.secondaryText && (
                <Box className="text-xs opacity-60">{s.secondaryText}</Box>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  )
}
